import { inject, Injectable, Injector } from "@angular/core";
import { collection, doc, Firestore, getDoc, getDocs, onSnapshot } from "@angular/fire/firestore";
import { Router } from "@angular/router";
import { CelebrationAnimationService } from "@core/celebration-animation.service";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { from, Observable } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { BaseService } from "../app.base.service";
import { CommonService } from "./common/common.service";
import { FirestoreDataService } from "./firestore-data.service";

@Injectable({
  providedIn: "root",
})
export class TrackedEventsService extends BaseService {
  public showCongratulations: boolean;
  listOfEvents$ = from(
    getDocs(collection(this.firestore, "event_lookup"))
  ).pipe(
    map((snapshot) => {
      return snapshot.docs.map((doc) => {
        return { id: doc.id };
      });
    })
  );
  wpUserId;
  private authService: AuthService;
  private authStateService: AuthStateService;
  private commonService= inject(CommonService);
  private firestoreDataService= inject(FirestoreDataService);
  constructor(private firestore: Firestore,
    injector: Injector, private router: Router,
    private celebrationAnimationService: CelebrationAnimationService) {
    super(injector);
    
    // Get AuthService and AuthStateService from injector
    this.authService = injector.get(AuthService);
    this.authStateService = injector.get(AuthStateService);

    this.commonService.getUid("TrackedEventsService").pipe(take(1)).subscribe({
      next: (userId) => {
        if (!userId || userId === "null") return;

        this.showCongratulations = true;
        const userDocRef = doc(this.firestore, `users/${userId}`);

        // ✅ FIX: Gate the onSnapshot listener on customTokenReady$ so it never
        // opens before the Firebase custom token is confirmed stored, preventing
        // the permission-denied race condition (LOTTOSOCIAL-PWA-1MED).
        this.authStateService.customTokenReady$.pipe(
          take(1),
          switchMap(() => new Observable<void>((subscriber) => {
            const unsubscribe = onSnapshot(
              userDocRef,
              (docSnapshot) => {
                const data = docSnapshot.data();
                if (!data || !data?.wp_user_id) return;
                this.wpUserId = data.wp_user_id;
              },
              (error) => {
                // For permission-denied errors (e.g. during iOS auth token refresh on
                // poor connections), silently log and complete rather than calling
                // subscriber.error(). Propagating the error triggers RxJS teardown
                // which calls unsubscribe() while Firestore is mid-error-processing,
                // causing an internal assertion failure (ID: ca9/b815) that surfaces
                // as an UnsubscriptionError (LOTTOSOCIAL-PWA-1MZ8).
                if ((error as any)?.code === 'permission-denied') {
                  this.authService.errorHandlerLogs("User data listener: permission-denied (suppressed to prevent UnsubscriptionError):", error);
                  subscriber.complete();
                } else {
                  this.authService.errorHandlerLogs("Error fetching user data:", error);
                  subscriber.error(error);
                }
              }
            );
            return () => {
              try {
                unsubscribe();
              } catch (e) {
                // Swallow any Firestore internal errors thrown during teardown
                // to prevent them from surfacing as RxJS UnsubscriptionError.
                console.warn("Error during user data Firestore listener teardown (suppressed):", e);
              }
            };
          }))
        ).subscribe({
          error: (err) => console.error("Error in user data listener:", err)
        });
      },
      error: (err) => {
        console.error("Error fetching userId from CommonService:", err);
      }
    });
  }

  updateTrackedUserEvent(eventName: string): void {
    this.commonService.getUid("TrackedEventsService").pipe(take(1)).subscribe({
      next: (userId) => {
        if (!userId || userId === "null") return;

        const path_id = "L2ipQoKMR3byqnJphC0T";
        const doc_data = {
          eventName,
          created_at: new Date()
        };
        const method = "create";

        this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          error: (error) => {
            console.error("Error adding tracked user event:", error);
            return false;
          }
        });
      },
      error: (err) => {
        console.error("Error fetching userId from CommonService:", err);
      }
    });
  }

  prepareDataForCelebration() {
    return {
      celebration: {
        type: "xp",
        xp_count: {
          f_xp: 0,
          g_xp: 0,
          c_xp: 0,
          v_xp: 0,
        },
      },
      content: {},
      updated_datetime: new Date(),
      processed: true,
    };
  }

  updateCelebration(event: any): void {
    let isUpdated: boolean = false;
    let gpObject: any;
    let xpReason: string;

    this.commonService.getUid("trackeventService ").pipe(take(1)).subscribe({
      next: (userId) => {
        if (!userId || userId === "null") return;

        const celebrationDocRef = doc(this.firestore, `users/${userId}/celebration/details`);

        getDoc(celebrationDocRef)
          .then((docSnapshot) => {
            const data = docSnapshot.data();
            if (!data || !data.celebration) return;

            if (!isUpdated) {
              switch (event?.xp_type) {
                case "f_xp":
                  data.celebration.xp_count.f_xp += event?.xp_amount;
                  xpReason = `award ${event?.xp_amount} points to user for fortune`;
                  break;
                case "g_xp":
                  data.celebration.xp_count.g_xp += event?.xp_amount;
                  xpReason = `award ${event?.xp_amount} points to user for playing games`;
                  break;
                case "c_xp":
                  data.celebration.xp_count.c_xp += event?.xp_amount;
                  xpReason = `award ${event?.xp_amount} points to user for community`;
                  break;
                case "v_xp":
                  data.celebration.xp_count.v_xp += event?.xp_amount;
                  xpReason = `award ${event?.xp_amount} points to user for VIP`;
                  break;
              }

              data.updated_datetime = new Date();
              data.processed = false;
              isUpdated = true;

              this.commonService.getV2CloudFunction("updatefirestoredoc", {
                path_id: "qf2ppeV1MCNZKWuIUawx",
                doc_data: data,
                method: "update",
              }).pipe(take(1)).subscribe({
                error: (error) => {
                  return error;
                }
              });
              gpObject = {
                user: this.wpUserId,
                points: event?.xp_amount,
                points_type: event?.xp_type,
                reason: xpReason,
                comment_post_id: event?.comment_postid,
              };

              this.sendDataToGamipress(gpObject).subscribe(() => {
                console.log("Gamipress data sent successfully.");
              });
            }
          })
          .catch((error) => {
            console.error("Error fetching celebration details:", error);
          });
      },
      error: (err) => {
        console.error("Error fetching userId from CommonService:", err);
      }
    });
  }



  sendDataToGamipress(data) {
    return this.gamipressFunction(data);
  }

  openAnimation() {
    // This method has been used to track Celebration Animation 
    this.celebrationAnimationService.trackCelebrations();
  }

}
